import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import {
  getOnboardingLiveSyncReport,
  type LiveSyncClientReport,
  type LiveSyncLocation,
} from "@/lib/onboarding-live-sync.functions";

export const Route = createFileRoute("/_authenticated/admin/onboarding-live-sync")({
  head: () => ({
    meta: [
      { title: "Onboarding Live Sync — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OnboardingLiveSyncPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-rose-600">Failed to load: {(error as Error).message}</div>
  ),
});

function Badge({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "bad" | "muted";
  children: React.ReactNode;
}) {
  const styles: Record<string, string> = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    bad: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${styles[tone]}`}
    >
      {children}
    </span>
  );
}

function LocationRow({ loc }: { loc: LiveSyncLocation }) {
  const tone: "ok" | "warn" | "bad" =
    loc.issues.length === 0
      ? "ok"
      : loc.status === "matched"
        ? "warn"
        : "bad";
  return (
    <tr className="border-t border-slate-100 align-top">
      <td className="px-3 py-2">
        <Badge tone={tone}>
          {loc.crm_is_primary ? "Primary" : "Extra"} · {loc.status}
        </Badge>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-slate-900">
          {loc.place_name ?? loc.crm_label ?? "—"}
        </div>
        <div className="text-xs text-slate-500">
          {loc.place_address ?? loc.crm_address ?? "—"}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        <div>
          <span className="text-slate-500">place_id:</span>{" "}
          <code className="text-[11px]">{loc.place_id?.slice(0, 8) ?? "—"}</code>
        </div>
        <div>
          <span className="text-slate-500">slug:</span> {loc.place_slug ?? "—"}
        </div>
      </td>
      <td className="px-3 py-2 text-xs">
        <div>{loc.place_section ?? "—"} · {loc.place_category ?? "—"}</div>
        <div className="text-slate-500">city: {loc.place_city_slug ?? loc.crm_city ?? "—"}</div>
      </td>
      <td className="px-3 py-2">
        {loc.place_published === true ? (
          <Badge tone="ok">Live</Badge>
        ) : loc.place_published === false ? (
          <Badge tone="warn">Draft</Badge>
        ) : (
          <Badge tone="muted">—</Badge>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {loc.issues.length ? (
          <ul className="list-disc pl-4 text-rose-600">
            {loc.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        ) : (
          <span className="text-emerald-600">OK</span>
        )}
      </td>
      <td className="px-3 py-2">
        {loc.place_slug && loc.place_section && (
          <a
            href={`/${loc.place_section}/${loc.place_slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-medium text-indigo-600 hover:underline"
          >
            Open live →
          </a>
        )}
      </td>
    </tr>
  );
}

function ClientCard({ c }: { c: LiveSyncClientReport }) {
  const totalIssues =
    c.issues.length +
    (c.primary?.issues.length ?? 0) +
    c.extras.reduce((n, r) => n + r.issues.length, 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900">
              {c.client_name ?? c.client_id.slice(0, 8)}
            </div>
            {c.segment && <Badge tone="muted">{c.segment}</Badge>}
            {c.listing_status && (
              <Badge
                tone={c.listing_status === "live" ? "ok" : "warn"}
              >
                {c.listing_status}
              </Badge>
            )}
            {totalIssues > 0 ? (
              <Badge tone="bad">{totalIssues} issue{totalIssues > 1 ? "s" : ""}</Badge>
            ) : (
              <Badge tone="ok">In sync</Badge>
            )}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Cap {c.allowed_locations} · Places {c.place_count} ({c.published_count} live) · CRM {c.crm_count}
          </div>
        </div>
        <a
          href={`/crm/${c.client_id}`}
          className="text-xs font-medium text-indigo-600 hover:underline"
        >
          Open CRM →
        </a>
      </div>
      {c.issues.length > 0 && (
        <div className="border-b border-rose-100 bg-rose-50/60 px-4 py-2 text-xs text-rose-700">
          {c.issues.map((i) => (
            <div key={i}>• {i}</div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Place</th>
              <th className="px-3 py-2">Section / Category</th>
              <th className="px-3 py-2">Published</th>
              <th className="px-3 py-2">Issues</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {c.primary && <LocationRow loc={c.primary} />}
            {c.extras.map((e, i) => (
              <LocationRow key={(e.place_id ?? e.crm_location_id ?? i) + ""} loc={e} />
            ))}
            {!c.primary && c.extras.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-xs text-slate-500">
                  No locations recorded.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OnboardingLiveSyncPage() {
  const fetchReport = useServerFn(getOnboardingLiveSyncReport);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "onboarding-live-sync"],
    queryFn: () => fetchReport(),
  });
  const [filter, setFilter] = useState<"all" | "issues" | "orphan" | "missing">(
    "all",
  );
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!data) return [] as LiveSyncClientReport[];
    const term = q.trim().toLowerCase();
    return data.clients.filter((c) => {
      if (term && !(c.client_name ?? "").toLowerCase().includes(term)) return false;
      if (filter === "issues") {
        const t =
          c.issues.length +
          (c.primary?.issues.length ?? 0) +
          c.extras.reduce((n, r) => n + r.issues.length, 0);
        if (t === 0) return false;
      }
      if (filter === "orphan") {
        const has = [c.primary, ...c.extras].some((r) => r?.status === "place_only");
        if (!has) return false;
      }
      if (filter === "missing") {
        const has = [c.primary, ...c.extras].some((r) => r?.status === "crm_only");
        if (!has) return false;
      }
      return true;
    });
  }, [data, filter, q]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Onboarding Live Sync
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Compares the primary place and all extra locations between the CRM
            and the live pages (published status, city, section, category,
            place_id).
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium hover:bg-slate-50"
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {isLoading && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
          Loading report…
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {(error as Error).message}
        </div>
      )}

      {data && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {[
              ["Clients", data.totals.clients],
              ["With Issues", data.totals.with_issues],
              ["Places", data.totals.places_total],
              ["Live", data.totals.published],
              ["Draft", data.totals.unpublished],
              ["Orphan (place only)", data.totals.orphan_places],
              ["Missing (CRM only)", data.totals.missing_places],
            ].map(([label, val]) => (
              <div
                key={String(label)}
                className="rounded-xl border border-slate-200 bg-white p-3"
              >
                <div className="text-[11px] uppercase tracking-wide text-slate-500">
                  {label}
                </div>
                <div className="mt-1 text-xl font-semibold text-slate-900">
                  {val}
                </div>
              </div>
            ))}
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            {(["all", "issues", "orphan", "missing"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`rounded-full border px-3 py-1 text-xs font-medium capitalize ${
                  filter === k
                    ? "border-indigo-600 bg-indigo-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {k}
              </button>
            ))}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client name…"
              className="ml-auto w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm"
            />
          </div>

          <div className="space-y-4">
            {filtered.length === 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                Nothing to show for this filter.
              </div>
            )}
            {filtered.map((c) => (
              <ClientCard key={c.client_id} c={c} />
            ))}
          </div>

          <div className="mt-6 text-[11px] text-slate-400">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        </>
      )}
    </div>
  );
}
